import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useClients } from '@/hooks/use-api-clients';
import { useTasks } from '@/hooks/use-api-tasks';
import { useNotes } from '@/hooks/use-api-notes';
import { useDocuments } from '@/hooks/use-api-documents';
import {
  isSearchable,
  normaliseTerm,
  partialSearchNote,
  searchDocuments,
  searchNotes,
  searchTasks,
  toClientResults,
  type SearchResult,
  type SearchResultType,
} from '@/lib/adapters/search';

const RECENT_SEARCHES_KEY = 'bookkeeping_recent_searches';
const MAX_RECENT_SEARCHES = 5;

/** Pause after the last keystroke before the term is sent to the API. */
const SEARCH_DEBOUNCE_MS = 250;
/** Clients are matched by the API, so a short page of matches is enough. */
const CLIENT_RESULT_LIMIT = 10;
/** The tasks, notes and documents routes have no search parameter: the newest rows (the API maximum per page) are filtered here. */
const CLIENT_SIDE_PAGE_LIMIT = 200;

/** Icon and colours for each result type. */
const TYPE_STYLE: Record<SearchResultType, { icon: string; iconColor: string; bgColor: string }> = {
  client: { icon: 'ri-user-line', iconColor: 'text-teal-600', bgColor: 'bg-teal-50' },
  task: { icon: 'ri-task-line', iconColor: 'text-blue-600', bgColor: 'bg-blue-50' },
  document: { icon: 'ri-file-line', iconColor: 'text-blue-600', bgColor: 'bg-blue-50' },
  note: { icon: 'ri-sticky-note-line', iconColor: 'text-violet-600', bgColor: 'bg-violet-50' },
};

/** `value`, updated only once it has stopped changing for `delayMs`. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function loadRecentSearches(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    const parsed: unknown = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

function persistRecentSearches(searches: string[] | null): void {
  try {
    if (searches === null) localStorage.removeItem(RECENT_SEARCHES_KEY);
    else localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
  } catch {
    // Storage can be unavailable (private window, blocked site data); recent searches are a convenience only.
  }
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** State of one result group: its own loading, error and rows, so one failing group never blanks the others. */
interface GroupState {
  key: string;
  title: string;
  icon: string;
  results: SearchResult[];
  /** First load, nothing to show yet. */
  isLoading: boolean;
  /** Any request in flight, including a refresh that keeps the previous rows on screen. */
  isFetching: boolean;
  error: string | null;
  /** Set when only the newest rows were searched, so a short or empty result is not the whole story. */
  partialNote: string | null;
  onRetry: () => void;
}

interface GlobalSearchProps {
  isOpen?: boolean;
  onClose?: () => void;
  setActiveSection?: (section: string) => void;
}

export default function GlobalSearch({ isOpen: isOpenProp, onClose }: GlobalSearchProps = {}) {
  // data-tour attribute for onboarding tour targeting
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenInternal;
  const setIsOpen = useCallback((v: boolean) => { setIsOpenInternal(v); if (!v && onClose) onClose(); }, [onClose]);
  const [query, setQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState<string[]>(loadRecentSearches);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmedQuery = normaliseTerm(query);
  const term = useDebouncedValue(trimmedQuery, SEARCH_DEBOUNCE_MS);
  // Nothing is requested until the palette is open and both the typed and the settled term are long enough.
  const searchEnabled = isOpen && isSearchable(trimmedQuery) && isSearchable(term);
  /** True while the typed term is long enough but the debounce has not produced a searchable term yet. */
  const isSettling = isOpen && isSearchable(trimmedQuery) && !isSearchable(term);

  const clientsQuery = useClients(
    { search: searchEnabled ? term : undefined, limit: CLIENT_RESULT_LIMIT },
    { enabled: searchEnabled },
  );
  const tasksQuery = useTasks({ limit: CLIENT_SIDE_PAGE_LIMIT }, { enabled: searchEnabled });
  const notesQuery = useNotes({ enabled: searchEnabled, limit: CLIENT_SIDE_PAGE_LIMIT });
  const documentsQuery = useDocuments({ limit: CLIENT_SIDE_PAGE_LIMIT }, { enabled: searchEnabled });

  const clientResults = useMemo(() => toClientResults(clientsQuery.data?.data ?? []), [clientsQuery.data]);
  const taskResults = useMemo(() => searchTasks(tasksQuery.data?.data ?? [], term), [tasksQuery.data, term]);
  const noteResults = useMemo(() => searchNotes(notesQuery.data?.data ?? [], term), [notesQuery.data, term]);
  const documentResults = useMemo(
    () => searchDocuments(documentsQuery.data?.data ?? [], term),
    [documentsQuery.data, term],
  );

  const groups: GroupState[] = [
    {
      key: 'clients', title: 'Clients', icon: 'ri-user-line', results: clientResults,
      isLoading: clientsQuery.isLoading, isFetching: clientsQuery.isFetching,
      error: clientsQuery.error ? clientsQuery.error.message : null, partialNote: null, onRetry: () => { void clientsQuery.refetch(); },
    },
    {
      key: 'tasks', title: 'Tasks', icon: 'ri-task-line', results: taskResults,
      isLoading: tasksQuery.isLoading, isFetching: tasksQuery.isFetching,
      error: tasksQuery.error ? tasksQuery.error.message : null,
      partialNote: tasksQuery.data ? partialSearchNote('tasks', tasksQuery.data.data.length, tasksQuery.data.pagination) : null, onRetry: () => { void tasksQuery.refetch(); },
    },
    {
      key: 'documents', title: 'Documents', icon: 'ri-file-line', results: documentResults,
      isLoading: documentsQuery.isLoading, isFetching: documentsQuery.isFetching,
      error: documentsQuery.error ? documentsQuery.error.message : null,
      partialNote: documentsQuery.data ? partialSearchNote('documents', documentsQuery.data.data.length, documentsQuery.data.pagination) : null, onRetry: () => { void documentsQuery.refetch(); },
    },
    {
      key: 'notes', title: 'Notes', icon: 'ri-sticky-note-line', results: noteResults,
      isLoading: notesQuery.isLoading, isFetching: notesQuery.isFetching,
      error: notesQuery.error ? notesQuery.error.message : null,
      partialNote: notesQuery.data ? partialSearchNote('notes', notesQuery.data.data.length, notesQuery.data.pagination) : null, onRetry: () => { void notesQuery.refetch(); },
    },
  ];

  // Flat list, in display order, for keyboard navigation. Only groups that are shown contribute.
  const allResults: SearchResult[] = searchEnabled ? groups.flatMap((g) => g.results) : [];
  const totalResults = allResults.length;
  const hasResults = totalResults > 0;
  const allEmpty = searchEnabled && groups.every((g) => !g.isFetching && !g.error && g.results.length === 0);
  const partialNotes = groups.flatMap((g) => (g.partialNote ? [g.partialNote] : []));

  // Save recent search
  const saveRecentSearch = (searchQuery: string) => {
    const value = normaliseTerm(searchQuery);
    if (!value) return;

    const updated = [value, ...recentSearches.filter((s) => s !== value)].slice(0, MAX_RECENT_SEARCHES);
    setRecentSearches(updated);
    persistRecentSearches(updated);
  };

  // Highlight matched text
  const highlightMatch = (text: string, highlightTerm: string) => {
    const needle = highlightTerm.trim();
    if (!needle) return text;

    // The capturing group puts every match at an odd index of the split.
    const parts = text.split(new RegExp(`(${escapeRegExp(needle)})`, 'gi'));
    return parts.map((part, i) =>
      i % 2 === 1 ?
        <mark key={i} className="bg-amber-200 text-gray-900 font-medium">{part}</mark> :
        part
    );
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setSelectedIndex(0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (allResults.length > 0) setSelectedIndex(prev => (prev + 1) % allResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (allResults.length > 0) setSelectedIndex(prev => (prev - 1 + allResults.length) % allResults.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (allResults[selectedIndex]) {
        handleResultClick(allResults[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    saveRecentSearch(query);

    // Navigate to the relevant section
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(`/?section=${result.section}&highlight=${encodeURIComponent(result.id)}`);
    }

    setIsOpen(false);
    setQuery('');
  };

  // Handle recent search click
  const handleRecentSearchClick = (search: string) => {
    setQuery(search);
    setSelectedIndex(0);
    inputRef.current?.focus();
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    persistRecentSearches(null);
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  // Index of each group's first row within the flat keyboard list.
  let runningIndex = 0;
  const groupStartIndex: Record<string, number> = {};
  for (const group of groups) {
    groupStartIndex[group.key] = runningIndex;
    runningIndex += group.results.length;
  }

  return (
    <div className="relative" ref={searchRef}>
      {/* Search Input */}
      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg"></i>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleSearchChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search clients, tasks, notes, documents..."
          aria-label="Search clients, tasks, notes, documents"
          className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setSelectedIndex(0);
            }}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-[600px] overflow-y-auto z-50">
          {/* Recent Searches */}
          {!query && recentSearches.length > 0 && (
            <div className="p-3 border-b border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recent Searches</span>
                <button
                  onClick={clearRecentSearches}
                  className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                >
                  Clear
                </button>
              </div>
              <div className="space-y-1">
                {recentSearches.map((search, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleRecentSearchClick(search)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors text-left"
                  >
                    <i className="ri-history-line text-gray-500"></i>
                    <span>{search}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Hint: nothing is requested until the term is long enough */}
          {!isSearchable(trimmedQuery) && (
            <div className="p-6 text-center">
              <p className="text-sm text-gray-500">Type at least 2 characters to search clients, tasks, notes and documents</p>
            </div>
          )}

          {/* Typed term is long enough but the debounce has not fired yet */}
          {isSettling && (
            <div className="p-6 text-center" role="status">
              <p className="text-sm text-gray-500">Searching...</p>
            </div>
          )}

          {/* No Results */}
          {allEmpty && (
            <div className="p-8 text-center">
              <i className="ri-search-line text-4xl text-gray-500 mb-2"></i>
              <p className="text-sm text-gray-500">No results for &ldquo;{term}&rdquo;</p>
              {partialNotes.map((note) => (
                <p key={note} className="text-xs text-gray-500 mt-1">{note}</p>
              ))}
            </div>
          )}

          {/* Results by Category: each group has its own loading and error state */}
          {searchEnabled && !allEmpty && (
            <div className="py-2">
              {groups.map((group) => {
                const visible = group.isLoading || group.error !== null || group.results.length > 0 || group.partialNote !== null;
                if (!visible) return null;
                return (
                  <ResultGroup
                    key={group.key}
                    group={group}
                    query={term}
                    highlightMatch={highlightMatch}
                    onResultClick={handleResultClick}
                    selectedIndex={selectedIndex}
                    startIndex={groupStartIndex[group.key]}
                  />
                );
              })}
            </div>
          )}

          {/* Footer with keyboard shortcuts */}
          {searchEnabled && hasResults && (
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-xs">↑↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-xs">Enter</kbd>
                  Open
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-xs">Esc</kbd>
                  Close
                </span>
              </div>
              <span>{totalResults} result{totalResults !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Result Group Component
interface ResultGroupProps {
  group: GroupState;
  query: string;
  highlightMatch: (text: string, query: string) => React.ReactNode;
  onResultClick: (result: SearchResult) => void;
  selectedIndex: number;
  startIndex: number;
}

function ResultGroup({
  group,
  query,
  highlightMatch,
  onResultClick,
  selectedIndex,
  startIndex
}: ResultGroupProps) {
  const { title, icon, results, isLoading, isFetching, error, partialNote, onRetry } = group;
  return (
    <div className="mb-1" role="group" aria-label={title}>
      <div className="px-3 py-2 flex items-center gap-2">
        <i className={`${icon} text-gray-500 text-sm`}></i>
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</span>
        {!isLoading && !error && <span className="text-xs text-gray-500">({results.length})</span>}
        {isFetching && !isLoading && (
          <i className="ri-loader-4-line animate-spin text-gray-400 text-sm" role="status" aria-label={`Refreshing ${title}`}></i>
        )}
      </div>
      {isLoading && (
        <div className="px-3 py-2 flex items-center gap-2 text-xs text-gray-500" role="status">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Searching {title.toLowerCase()}...</span>
        </div>
      )}
      {error && (
        <div className="px-3 py-2 flex items-center justify-between gap-2 text-xs text-red-600" role="alert">
          <span className="truncate">Could not load {title.toLowerCase()}: {error}</span>
          <button
            onClick={onRetry}
            className="text-teal-600 hover:text-teal-700 font-medium flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}
      {partialNote && !isLoading && !error && (
        <p className="px-3 pb-1 text-xs text-gray-500">{partialNote}</p>
      )}
      <div>
        {results.map((result, idx) => {
          const globalIndex = startIndex + idx;
          const isSelected = globalIndex === selectedIndex;
          const style = TYPE_STYLE[result.type];

          return (
            <button
              key={result.id}
              onClick={() => onResultClick(result)}
              className={`w-full px-3 py-2.5 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left ${
                isSelected ? 'bg-teal-50' : ''
              }`}
            >
              <div className={`w-9 h-9 flex items-center justify-center ${style.bgColor} rounded-lg flex-shrink-0`}>
                <i className={`${style.icon} ${style.iconColor} text-lg`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {highlightMatch(result.title, query)}
                </div>
                <div className="text-xs text-gray-600 truncate">
                  {highlightMatch(result.subtitle, query)}
                </div>
              </div>
              {isSelected && (
                <i className="ri-arrow-right-line text-teal-600 text-lg flex-shrink-0"></i>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
